import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    Stack, 
    ToggleButtonGroup,
    ToggleButton,
    Container,
    Alert,
    Avatar,
    Autocomplete,
    IconButton
} from "@mui/material";
import { 
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon 
} from '@mui/icons-material';
import { getOrganizationForJoinRequest, createJoinRequest, searchOrganizationsByName } from '../services/joinRequestService';
import OnboardingHeader from '../components/OnboardingHeader';

function SearchOrganizationPage() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    
    const [searchType, setSearchType] = useState("name");
    const [searchQuery, setSearchQuery] = useState('');
    const [foundOrganization, setFoundOrganization] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState('');
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);

    const handleSearchTypeChange = (event, newSearchType) => {
        if (newSearchType !== null) {
            setSearchType(newSearchType);
            setSearchQuery('');
            setFoundOrganization(null);
            setError('');
            setAutocompleteOptions([]);
        }
    };

    // Debounced search for name autocomplete
    useEffect(() => {
        if (searchType === 'name' && searchQuery.trim() && searchQuery.length >= 2) {
            const timeoutId = setTimeout(async () => {
                setAutocompleteLoading(true);
                try {
                    const results = await searchOrganizationsByName(searchQuery);
                    setAutocompleteOptions(results.organizations || []);
                } catch (err) {
                    console.error('Error searching organizations:', err);
                    setAutocompleteOptions([]);
                } finally {
                    setAutocompleteLoading(false);
                }
            }, 300); // 300ms delay

            return () => clearTimeout(timeoutId);
        } else {
            setAutocompleteOptions([]);
        }
    }, [searchQuery, searchType]);

    const handleSearchOrganization = async () => {
        if (!searchQuery.trim()) {
            setError('Please enter a search query');
            return;
        }

        setSearchLoading(true);
        setError('');

        try {
            const results = await getOrganizationForJoinRequest(searchQuery.trim(), searchType);
            
            if (results.type === 'single') {
                setFoundOrganization(results.organization);
            } else {
                // For multiple results, user needs to select from autocomplete
                setError('Please select an organization from the suggestions');
                setFoundOrganization(null);
            }
            
            if (results.type === 'single') {
                setError('');
            }
        } catch (err) {
            console.error('Error searching organization:', err);
            setError(err.response?.data?.error || 'Organization not found');
            setFoundOrganization(null);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAutocompleteChange = (event, value) => {
        if (value) {
            setSearchQuery(value.name);
            setFoundOrganization(value);
            setError('');
        } else {
            setFoundOrganization(null);
        }
    };

    const handleJoinOrganization = async () => {
        if (!foundOrganization) {
            setError('Please search for an organization first');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await createJoinRequest(foundOrganization._id);
            
            enqueueSnackbar('Join request submitted successfully! The organization admin will review your request.', { variant: 'success' });
            
            // Navigate back to organization choice page
            navigate('/organization-choice');
        } catch (err) {
            console.error('Error submitting join request:', err);
            setError(err.response?.data?.error || 'Failed to submit join request');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/organization-choice');
    };

    return (
        <>
            <OnboardingHeader />
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                {/* Back Button */}
                <Box sx={{ mb: 3 }}>
                    <IconButton 
                        onClick={handleBack}
                        sx={{ 
                            color: 'text.primary',
                            '&:hover': { bgcolor: 'action.hover' }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Box>

                {/* Main Content */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Stack 
                        width="743px" 
                        alignItems="center" 
                        spacing={10}
                        sx={{ maxWidth: '100%' }}
                    >
                        <Stack spacing={8.5} width="100%">
                            <Typography 
                                variant="h1" 
                                sx={{
                                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    fontSize: "50px",
                                    lineHeight: "65px",
                                    textAlign: 'center'
                                }}
                            >
                                Join an existing organization
                            </Typography>

                            <Stack spacing={7} width="100%" alignItems="center">
                                <Stack spacing={2.5} width="100%" alignItems="center">
                                    <Box width="396px" sx={{ maxWidth: '100%' }}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{
                                                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                                fontWeight: 500,
                                                color: 'text.primary',
                                                textAlign: "center",
                                                mb: 2.5
                                            }}
                                        >
                                            Search for an organization name/ID
                                        </Typography>

                                        <ToggleButtonGroup
                                            value={searchType}
                                            exclusive
                                            onChange={handleSearchTypeChange}
                                            fullWidth
                                            sx={{ 
                                                height: "52px", 
                                                bgcolor: "#f5f5f5", 
                                                borderRadius: "6px",
                                                border: 'none',
                                                position: 'relative',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: "6px",
                                                    zIndex: 2,
                                                    transition: 'all 0.3s ease',
                                                    '&.Mui-selected': {
                                                        bgcolor: 'transparent',
                                                    }
                                                }
                                            }}
                                        >
                                            {/* Animated background indicator */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: searchType === 'name' ? 0 : '50%',
                                                    width: '50%',
                                                    height: '100%',
                                                    bgcolor: '#E2F1FF',
                                                    borderRadius: '6px',
                                                    transition: 'left 0.3s ease-in-out',
                                                    zIndex: 1,
                                                }}
                                            />
                                            
                                            <ToggleButton 
                                                value="name" 
                                                sx={{ 
                                                    height: "52px",
                                                    bgcolor: "transparent",
                                                    color: searchType === "name" ? "#218ef4" : "#353535",
                                                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                                    fontWeight: searchType === "name" ? 500 : 400,
                                                    fontSize: "16px",
                                                    "&.Mui-selected": {
                                                        bgcolor: "transparent",
                                                        color: "#218ef4",
                                                        "&:hover": {
                                                            bgcolor: "transparent",
                                                        }
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "transparent",
                                                    }
                                                }}
                                            >
                                                Search by name
                                            </ToggleButton>
                                            <ToggleButton 
                                                value="id" 
                                                sx={{ 
                                                    height: "52px",
                                                    bgcolor: "transparent",
                                                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                                    fontWeight: searchType === "id" ? 500 : 400,
                                                    fontSize: "16px",
                                                    color: searchType === "id" ? "#218ef4" : "#353535",
                                                    "&.Mui-selected": {
                                                        bgcolor: "transparent",
                                                        color: "#218ef4",
                                                        "&:hover": {
                                                            bgcolor: "transparent",
                                                        }
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "transparent",
                                                    }
                                                }}
                                            >
                                                Search by ID
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>

                                    {/* Search Input - Conditional rendering based on search type */}
                                    <Box width="100%" sx={{ maxWidth: '743px' }}>
                                        {searchType === 'id' ? (
                                            <TextField
                                                fullWidth
                                                placeholder="Enter organization's ID"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: "60px",
                                                        padding: "0",
                                                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                                        fontSize: "16px",
                                                        color: "text.secondary",
                                                        '& fieldset': {
                                                            borderColor: "divider",
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: "primary.main",
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: "primary.main",
                                                        },
                                                        '& input': {
                                                            padding: "18px 20px",
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Autocomplete
                                                options={autocompleteOptions}
                                                loading={autocompleteLoading}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                                onChange={handleAutocompleteChange}
                                                onInputChange={(event, newInputValue) => {
                                                    setSearchQuery(newInputValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        placeholder="Enter organization's name"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                height: "60px",
                                                                padding: "0",
                                                                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                                                fontSize: "16px",
                                                                color: "text.secondary",
                                                                '& fieldset': {
                                                                    borderColor: "divider",
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: "primary.main",
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: "primary.main",
                                                                },
                                                                '& input': {
                                                                    padding: "18px 20px !important",
                                                                    paddingRight: "20px !important",
                                                                },
                                                                // Fix autocomplete dropdown icon spacing
                                                                '& .MuiAutocomplete-endAdornment': {
                                                                    right: '14px',
                                                                },
                                                                // Ensure input has consistent padding
                                                                '& .MuiInputBase-input': {
                                                                    padding: "18px 20px !important",
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                                            <Avatar
                                                                src={option.logo?.thumbnailUrl || option.logo?.url}
                                                                sx={{ width: 40, height: 40 }}
                                                            >
                                                                <BusinessIcon color="primary" />
                                                            </Avatar>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {option.name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {option.city && option.country ? `${option.city}, ${option.country}` : 
                                                                     option.city ? option.city : 
                                                                     option.country ? option.country : 
                                                                     'Location not specified'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </li>
                                                )}
                                                noOptionsText={
                                                    searchQuery.length < 2 
                                                        ? "Type at least 2 characters to search" 
                                                        : "No organizations found"
                                                }
                                            />
                                        )}
                                    </Box>
                                </Stack>

                                {/* Error Display */}
                                {error && (
                                    <Alert severity="error" sx={{ width: '100%', maxWidth: '743px' }}>
                                        {error}
                                    </Alert>
                                )}

                                {/* Found Organization Display */}
                                {foundOrganization && (
                                    <Alert severity="success" sx={{ width: '100%', maxWidth: '743px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                src={foundOrganization.logo?.thumbnailUrl || foundOrganization.logo?.url}
                                                sx={{ width: 32, height: 32 }}
                                            >
                                                <BusinessIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body1">
                                                    Organization found: <strong>{foundOrganization.name}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {foundOrganization.city && foundOrganization.country ? `${foundOrganization.city}, ${foundOrganization.country}` : 
                                                     foundOrganization.city ? foundOrganization.city : 
                                                     foundOrganization.country ? foundOrganization.country : 
                                                     'Location not specified'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Alert>
                                )}

                                {/* Search/Join Button */}
                                <Button
                                    variant="contained"
                                    onClick={foundOrganization ? handleJoinOrganization : handleSearchOrganization}
                                    disabled={loading || searchLoading || !searchQuery.trim()}
                                    sx={{
                                        width: "279px",
                                        height: "60px",
                                        bgcolor: "primary.main",
                                        borderRadius: "6px",
                                        boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "16px",
                                        textTransform: "none",
                                        "&:hover": {
                                            bgcolor: "primary.dark"
                                        },
                                        "&:disabled": {
                                            bgcolor: "action.disabledBackground",
                                            color: "action.disabled"
                                        }
                                    }}
                                >
                                    {loading ? 'Submitting...' : 
                                     searchLoading ? 'Searching...' : 
                                     foundOrganization ? 'Submit Join Request' : 'Search'}
                                </Button>
                            </Stack>
                        </Stack>

                        {/* Join with link section */}
                        <Box width="425px" sx={{ maxWidth: '100%' }}>
                            <Typography 
                                variant="body1" 
                                sx={{
                                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                    fontWeight: 400,
                                    color: "text.secondary",
                                    textAlign: "center",
                                    mb: 1
                                }}
                            >
                                Or Join with link
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{
                                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                                    fontWeight: 400,
                                    color: "text.primary",
                                    fontSize: "16px",
                                    textAlign: "center"
                                }}
                            >
                                Ask your admin for a join link - just click and you're in!
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Container>
        </>
    );
}

export default SearchOrganizationPage;